/**
 * ブラウザ保存（localStorage）の共通ユーティリティ。
 *
 * このサイトはアカウント登録・ログインを行わない方針のため、
 * ユーザーが明示的に保存したデータだけを localStorage に置く。
 * 閲覧履歴・検索履歴の自動保存は行わない。
 *
 * 既存キーは変更しない（保存済みデータを消さないため）。
 */
export const STORAGE_KEYS = {
  favorites: "worksnavi:favorites",
  compare: "worksnavi:compare",
  watchLater: "worksnavi:watchLater",
  mode: "worksnavi:mode",
  /** ユーザーが明示的に保存した検索条件（既存キーの追加のみ・変更なし） */
  savedSearches: "worksnavi:savedSearches",
  /** バックアップのダウンロードを開始した日時（新規キー。既存データには影響しない） */
  lastBackupAt: "worksnavi:lastBackupAt",
} as const;

export type StorageKeyName = keyof typeof STORAGE_KEYS;

/** localStorage が使えるか（プライベートモードや容量制限で失敗する環境がある） */
export function isLocalStorageAvailable(): boolean {
  if (typeof window === "undefined") return false;
  try {
    const probe = "__yumesearch_probe__";
    window.localStorage.setItem(probe, "1");
    window.localStorage.removeItem(probe);
    return true;
  } catch {
    return false;
  }
}

export function safeReadString(key: string): string | null {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

export function safeWriteString(key: string, value: string): boolean {
  if (typeof window === "undefined") return false;
  try {
    window.localStorage.setItem(key, value);
    return true;
  } catch {
    return false;
  }
}

export function safeRemove(key: string): boolean {
  if (typeof window === "undefined") return false;
  try {
    window.localStorage.removeItem(key);
    return true;
  } catch {
    return false;
  }
}

export interface AtomicWriteEntry {
  key: string;
  value: string;
}

export interface AtomicWriteResult {
  ok: boolean;
  /** 書き込みに失敗したキー（失敗時の復旧対象） */
  failedKey?: string;
  /** 失敗時に、書き込み前の値へ完全に戻せたか。false の場合は復旧も失敗している。 */
  rollbackOk: boolean;
}

/**
 * 複数キーをまとめて書き込む。
 * 途中で1つでも失敗したら、書き込み前の値へすべて巻き戻す。
 * 書き込み後は読み戻して内容が一致するかまで確認する。
 * インポートのように「全部成功 or 何もしない」を保証したい処理で使う。
 *
 * localStorage にはトランザクションがないため、完全な保証ではない。
 * 失敗時は rollbackOk を見て、復旧できたかを必ず判定すること。
 */
export function writeEntriesAtomically(entries: AtomicWriteEntry[]): AtomicWriteResult {
  if (typeof window === "undefined") {
    return { ok: false, failedKey: entries[0]?.key, rollbackOk: true };
  }

  const snapshot = entries.map((entry) => ({ key: entry.key, value: safeReadString(entry.key) }));

  for (const entry of entries) {
    const written = safeWriteString(entry.key, entry.value) && safeReadString(entry.key) === entry.value;
    if (!written) {
      let rollbackOk = true;
      snapshot.forEach((previous) => {
        if (previous.value === null) {
          safeRemove(previous.key);
          if (safeReadString(previous.key) !== null) rollbackOk = false;
        } else {
          safeWriteString(previous.key, previous.value);
          if (safeReadString(previous.key) !== previous.value) rollbackOk = false;
        }
      });
      return { ok: false, failedKey: entry.key, rollbackOk };
    }
  }

  return { ok: true, rollbackOk: true };
}

/** 文字列IDの配列だけを安全に読み出す。壊れていれば空配列。 */
export function readStoredIdList(key: string): string[] {
  const raw = safeReadString(key);
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === "string") : [];
  } catch {
    return [];
  }
}

/**
 * 作品IDの種類。
 * - demo: 現在のダミーデータ（WK-0001 形式）
 * - external: 将来の外部APIの商品ID（ext: 接頭辞 / 数字のみ / Shopify GID）
 * - unknown: どちらにも該当しない値（手入力・破損データ）
 */
export type WorkIdKind = "demo" | "external" | "unknown";

export const DEMO_ID_PATTERN = /^WK-\d{4}$/i;
export const EXTERNAL_ID_PREFIX = "ext:";

export function classifyWorkId(id: string): WorkIdKind {
  const value = id.trim();
  if (DEMO_ID_PATTERN.test(value)) return "demo";
  if (value.startsWith(EXTERNAL_ID_PREFIX)) return "external";
  if (/^\d{5,}$/.test(value)) return "external";
  if (value.startsWith("gid://")) return "external";
  return "unknown";
}

/** 外部APIの商品IDをこのサイトの識別子に変換する（データ移行時の共通入口） */
export function toStoredWorkId(rawId: string | number): string {
  const value = String(rawId).trim();
  if (DEMO_ID_PATTERN.test(value)) return value.toUpperCase();
  if (value.startsWith(EXTERNAL_ID_PREFIX) || value.startsWith("gid://")) return value;
  return `${EXTERNAL_ID_PREFIX}${value}`;
}

/** 保存してよいIDか（長すぎる値・制御文字を弾く） */
export function isValidStoredId(id: unknown): id is string {
  if (typeof id !== "string") return false;
  const value = id.trim();
  if (value.length === 0 || value.length > 128) return false;
  for (let index = 0; index < value.length; index += 1) {
    const code = value.charCodeAt(index);
    if (code < 32 || code === 127) return false;
  }
  return true;
}

export function dedupeIds(ids: string[]): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  ids.forEach((id) => {
    const value = id.trim();
    const key = value.toLowerCase();
    if (!isValidStoredId(value) || seen.has(key)) return;
    seen.add(key);
    result.push(value);
  });
  return result;
}

/** 保存データの概算サイズ（バイト）。設定画面の表示用。 */
export function estimateStorageBytes(): number {
  return Object.values(STORAGE_KEYS).reduce((total, key) => {
    const raw = safeReadString(key);
    return total + (raw ? new Blob([raw]).size : 0);
  }, 0);
}

export function formatBytes(bytes: number): string {
  if (bytes <= 0) return "0 B";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}